// ============================================================================
// Status vocabularies for the Clinical Notes Engine and downstream orders.
//
// Keep these aligned with the Django models:
// - Encounter.status    : scheduled | draft | submitted | closed | amended
// - *Order.status       : pending | routed | accepted | in_progress |
//                         rejected | cancelled | completed | dispensed
// ============================================================================

export const ENCOUNTER_STATUS = Object.freeze({
  // Provisioned by a Booking but not yet opened by the doctor. The
  // CNE editor flips this to DRAFT on first mount so the row moves
  // from the doctor's "Upcoming" tab into "My Drafts" automatically.
  SCHEDULED: 'scheduled',
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  // Set when the linked Booking is cancelled — read-only, kept for
  // audit. Distinct from SUBMITTED (which is a finished consultation).
  CLOSED: 'closed',
  AMENDED: 'amended',
})

export const ORDER_STATUS = Object.freeze({
  PENDING: 'pending',
  ROUTED: 'routed',
  ACCEPTED: 'accepted',
  IN_PROGRESS: 'in_progress',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled',
  COMPLETED: 'completed',
  DISPENSED: 'dispensed',
})

// An encounter is editable while it's still pre-final. Two states
// allow writes:
//   * SCHEDULED — the booking just landed, the doctor's about to start
//     the consultation. Open the editor → write away. The mount-time
//     auto-transition (see EncounterEditor.jsx) flips this to DRAFT
//     so the "Upcoming" tab clears and "Drafts" picks it up.
//   * DRAFT     — the consultation is in progress.
// Anything terminal (SUBMITTED, CLOSED, AMENDED) is locked. The CNE
// editor renders a read-only banner and addendum-only path for those.
const _UNLOCKED = new Set([ENCOUNTER_STATUS.SCHEDULED, ENCOUNTER_STATUS.DRAFT])
export function isEncounterLocked(status) {
  return !_UNLOCKED.has((status || '').toLowerCase())
}

// Allowed lab-queue transitions, used by laboratory/LabOrderDetail.jsx.
// Doctors create orders at status="pending" (the model default), so the lab
// queue's first action is to Accept (or Reject) from pending. We keep
// `routed` in the table too — it's reserved for a future router that will
// push pending orders onto specific labs.
export const LAB_TRANSITIONS = Object.freeze({
  [ORDER_STATUS.PENDING]: [
    { label: 'Accept', next: ORDER_STATUS.ACCEPTED },
    { label: 'Reject', next: ORDER_STATUS.REJECTED },
  ],
  [ORDER_STATUS.ROUTED]: [
    { label: 'Accept', next: ORDER_STATUS.ACCEPTED },
    { label: 'Reject', next: ORDER_STATUS.REJECTED },
  ],
  [ORDER_STATUS.ACCEPTED]: [
    { label: 'Start work', next: ORDER_STATUS.IN_PROGRESS },
  ],
  [ORDER_STATUS.IN_PROGRESS]: [
    { label: 'Mark complete', next: ORDER_STATUS.COMPLETED },
  ],
})

// Radiology uses the same order-status vocabulary as lab.
export const IMAGING_TRANSITIONS = LAB_TRANSITIONS

// Prescription statuses follow the Prescription.STATUS_CHOICES in the
// backend model: draft -> active -> dispensed (or cancelled). Pharmacy
// staff see orders in "active" and move them to "dispensed".
export const PRESCRIPTION_STATUS = Object.freeze({
  DRAFT: 'draft',
  ACTIVE: 'active',
  DISPENSED: 'dispensed',
  CANCELLED: 'cancelled',
})

export const PRESCRIPTION_TRANSITIONS = Object.freeze({
  [PRESCRIPTION_STATUS.ACTIVE]: [
    { label: 'Dispense', next: PRESCRIPTION_STATUS.DISPENSED },
    { label: 'Cancel', next: PRESCRIPTION_STATUS.CANCELLED },
  ],
})

// Ambulance statuses are free-text in the backend; the sequenced set we
// surface in the UI is requested -> accepted -> dispatched -> completed,
// plus rejected/cancelled branches.
export const AMBULANCE_STATUS = Object.freeze({
  REQUESTED: 'requested',
  ACCEPTED: 'accepted',
  DISPATCHED: 'dispatched',
  COMPLETED: 'completed',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled',
})

export const AMBULANCE_TRANSITIONS = Object.freeze({
  [AMBULANCE_STATUS.REQUESTED]: [
    { label: 'Accept', next: AMBULANCE_STATUS.ACCEPTED },
    { label: 'Reject', next: AMBULANCE_STATUS.REJECTED },
  ],
  [AMBULANCE_STATUS.ACCEPTED]: [
    { label: 'Dispatch', next: AMBULANCE_STATUS.DISPATCHED },
  ],
  [AMBULANCE_STATUS.DISPATCHED]: [
    { label: 'Mark complete', next: AMBULANCE_STATUS.COMPLETED },
  ],
})
